import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import api from "../../api/sellerApi";

export default function MyDeliveries() {
  const [orders, setOrders] = useState([]);
  const [period, setPeriod] = useState("week");
  const [statusFilter, setStatusFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    setLoading(true);
    Promise.all([api.get(`/orders?period=${period}`), api.get("/stats")])
      .then(([o, s]) => {
        setOrders(o.data);
        setStats(s.data);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [period]);

  const filteredOrders = useMemo(() => {
    return orders.filter((o) => {
      if (statusFilter === "active") {
        if (
          !["PENDING", "ASSIGNED", "PICKED_UP", "IN_TRANSIT"].includes(o.status)
        )
          return false;
      } else if (statusFilter !== "all" && o.status !== statusFilter) {
        return false;
      }
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase().trim();
        const matchBuyer = (o.buyer_name || "").toLowerCase().includes(q);
        const matchAddr = (o.dropoff_address || "").toLowerCase().includes(q);
        if (!matchBuyer && !matchAddr) return false;
      }
      return true;
    });
  }, [orders, statusFilter, searchQuery]);

  const statusColors = {
    PENDING: "bg-gray-100 text-gray-600",
    ASSIGNED: "bg-blue-100 text-blue-700",
    PICKED_UP: "bg-yellow-100 text-yellow-700",
    IN_TRANSIT: "bg-purple-100 text-purple-700",
    DELIVERED: "bg-green-100 text-green-700",
    CANCELLED: "bg-red-100 text-red-600",
  };

  return (
    <div className="min-h-screen bg-gray-50 px-5 py-6">
      <h1 className="text-lg font-bold text-gray-900 mb-1">My Deliveries</h1>

      {/* Monthly summary */}
      {stats && (
        <div className="bg-green-50 rounded-xl p-3 mb-4 flex justify-between items-center">
          <div>
            <p className="text-sm text-green-700 font-medium">
              {stats.deliveries_this_month} deliveries this month
            </p>
            <p className="text-xs text-green-600">{stats.discount_tier}</p>
          </div>
          {stats.deliveries_until_next_tier > 0 && (
            <p className="text-xs text-green-600">
              {stats.deliveries_until_next_tier} more for next tier
            </p>
          )}
        </div>
      )}

      {/* Period filter */}
      <div className="flex gap-2 mb-3">
        {[
          ["week", "This Week"],
          ["month", "This Month"],
          ["all", "All Time"],
        ].map(([key, label]) => (
          <button
            key={key}
            onClick={() => setPeriod(key)}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium ${period === key ? "bg-green-600 text-white" : "bg-white border border-gray-200 text-gray-600"}`}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Status Filter Chips */}
      <div className="flex gap-1.5 overflow-x-auto pb-1 mb-3 no-scrollbar">
        {[
          ["all", "All"],
          ["active", "Active"],
          ["DELIVERED", "Delivered"],
          ["CANCELLED", "Cancelled"],
        ].map(([key, label]) => (
          <button
            key={key}
            onClick={() => setStatusFilter(key)}
            className={`px-2.5 py-1 rounded-full text-xs whitespace-nowrap font-medium transition-colors ${statusFilter === key ? "bg-gray-900 text-white" : "bg-gray-200/70 text-gray-600 hover:bg-gray-200"}`}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Search Bar */}
      <div className="mb-4">
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search by buyer name or address..."
          className="w-full bg-white border border-gray-200 rounded-xl px-3 py-2 text-xs text-gray-800 focus:outline-none focus:ring-1 focus:ring-green-500"
        />
      </div>

      {loading ? (
        <div className="flex justify-center py-8">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-green-600" />
        </div>
      ) : (
        <div className="space-y-2">
          {filteredOrders.map((o) => (
            <Link
              key={o.id}
              to={`/seller/order/${o.id}`}
              className="block bg-white rounded-xl border border-gray-100 p-3.5 hover:border-gray-200 transition-colors"
            >
              <div className="flex justify-between items-start">
                <div>
                  <p className="font-medium text-gray-900 text-sm">
                    {o.buyer_name}
                  </p>
                  <p className="text-xs text-gray-500">{o.dropoff_address}</p>
                  <p className="text-xs text-gray-400 mt-1">
                    {new Date(o.created_at).toLocaleDateString()}
                  </p>
                </div>
                <div className="text-right">
                  <span
                    className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusColors[o.status]}`}
                  >
                    {o.status}
                  </span>
                  <p className="text-sm font-bold text-gray-900 mt-1">
                    ${parseFloat(o.final_fee).toFixed(2)}
                  </p>
                </div>
              </div>
            </Link>
          ))}
          {filteredOrders.length === 0 && (
            <div className="text-center py-8">
              <p className="text-gray-400 text-sm">
                {searchQuery.trim()
                  ? `No orders match "${searchQuery}"`
                  : statusFilter !== "all"
                    ? `No ${statusFilter.toLowerCase()} orders for this period`
                    : "No deliveries for this period"}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
